/* global SideDrawer, trapFocus, parseCartApiError, changeCartLine */

/**
 * Toggles the same loading state used by product card add to cart buttons.
 * @param {HTMLButtonElement} button - Button to update.
 * @param {boolean} isLoading - Whether the button is loading.
 */
function setAssortmentOpenerLoading(button, isLoading) {
  if (!button?.matches?.('button')) return;

  if (isLoading) {
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');
  } else {
    button.removeAttribute('aria-disabled');
    button.classList.remove('is-loading');
  }
}

if (!customElements.get('assortment-drawer')) {
  // How the drawer was opened: adding a new bundle, or editing one in the cart.
  const MODE_ADD = 'add';
  const MODE_EDIT = 'edit';
  const QUANTITY_ROUNDED_MESSAGE_MS = 5000;
  const QUANTITY_ROUNDED_ANNOUNCE_DELAY_MS = 125;
  // Head start given to a row message before the footer warning opens over it.
  const CASE_FULL_WARNING_DELAY_MS = 2000;

  class AssortmentDrawer extends SideDrawer {
    constructor() {
      super();

      this.mode = MODE_ADD;
      this.isPopulating = false;
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:assortment-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:assortment-drawer:close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.removeEventListener('click', this.handleDrawerClick);
      this.removeEventListener('click', this.handleAssortmentQuantityClick, true);
      this.removeEventListener('change', this.handleQuantityChange);
      this.removeEventListener('input', this.handleQuantityChange);
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('dispatch:assortment-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:assortment-drawer:close', this.closeDrawerViaEventHandler);

      this.handleDrawerClick = this.handleDrawerClick.bind(this);
      this.addEventListener('click', this.handleDrawerClick);

      this.handleAssortmentQuantityClick = this.handleAssortmentQuantityClick.bind(this);
      this.addEventListener('click', this.handleAssortmentQuantityClick, true);

      this.handleQuantityChange = this.handleQuantityChange.bind(this);
      this.addEventListener('change', this.handleQuantityChange);

      this.cartDrawer = document.querySelector('cart-drawer');
      this.handleCancelClick = this.handleCancelClick.bind(this);
      this.cancelButton = this.querySelector('.js-assortment-drawer-cancel');
      this.cancelButton?.addEventListener('click', this.handleCancelClick);

      this.warningMessage = this.querySelector('[data-warning="case-full"]');
      this.warningMessage?.querySelector('.js-close-warning')?.addEventListener('click', () => {
        this.setWarningVisibility(false);
      });

      this.overCapWarning = this.querySelector('[data-warning="over-cap"]');
      this.overCapWarningHome = this.overCapWarning?.parentElement;
    }

    /**
     * Handles clicks within the assortment drawer.
     * @param {object} evt - Event object.
     */
    handleDrawerClick(evt) {
      const submitBtn = evt.target.closest('.js-assortment-drawer-add');
      if (submitBtn && this.contains(submitBtn)) {
        this.handleAddToCart(evt);
      }
    }

    /**
     * Adjusts assortment variant quantities by increment, capped by remaining addable stock.
     * Runs in capture phase so it overrides theme quantity-input stepUp/stepDown behavior.
     * @param {object} evt - Event object.
     */
    handleAssortmentQuantityClick(evt) {
      const btn = evt.target.closest('.qty-input__btn');
      if (!btn || !this.contains(btn)) return;

      const input = btn.closest('[data-assortment-qty]')?.querySelector(
        '.js-assortment-component-qty, .js-assortment-case-qty'
      );
      if (!input || input.disabled) return;

      evt.preventDefault();
      evt.stopImmediatePropagation();

      const inc = Number.parseInt(input.dataset.increment, 10)
        || Number.parseInt(input.step, 10)
        || 1;
      let value = Number.parseInt(input.value, 10) || 0;
      const min = AssortmentDrawer.getInputMin(input);
      const max = this.getInputMaxAddable(input);

      if (btn.name === 'plus') {
        const room = max != null && !Number.isNaN(max) ? Math.max(0, max - value) : inc;
        value += Math.min(inc, room);
      } else if (btn.name === 'minus') {
        value = Math.max(min, value - inc);
      }

      if (value < min) value = min;
      if (max != null && !Number.isNaN(max)) value = Math.min(value, max);

      input.value = value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent(evt) {
      if (!evt?.detail?.opener) return;

      this.open(evt.detail.opener, null, {
        mode: evt.detail.mode,
        productHandle: evt.detail.productHandle,
        quantity: evt.detail.quantity,
        components: evt.detail.components
      });
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     * @param {string|object} [productHandleOrOptions] - Product handle, or options object. In
     * 'edit' mode it also carries the cart line to replace and its component quantities.
     */
    async open(opener, elementToFocus, productHandleOrOptions) {
      setAssortmentOpenerLoading(opener, true);
      const options = typeof productHandleOrOptions === 'string'
        ? { productHandle: productHandleOrOptions }
        : (productHandleOrOptions || {});
      const {
        mode, productHandle, quantity, components
      } = options;

      this.setMode(mode);
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      try {
        if (productHandle) {
          const content = await AssortmentDrawer.fetchProductData(
            productHandle,
            this.getAssortmentContentView()
          );
          this.populateDrawer(content, { quantity, components });
        } else {
          this.resetDrawerContent();
        }
      } catch (error) {
        this.resetDrawerContent();
        setAssortmentOpenerLoading(opener, false);
        if (overlay) overlay.style.transitionDelay = '';
        return;
      }

      setAssortmentOpenerLoading(opener, false);
      super.open(opener, elementToFocus);

      if (overlay) overlay.style.transitionDelay = '';
    }

    /**
     * Closes the drawer, handing the overlay and the focus trap back to the cart drawer when it is
     * still open behind this one. Both drawers share one overlay, which SideDrawer hides on close.
     * @param {Function} [callback] - Called after the close has completed.
     */
    close(callback) {
      const { opener } = this;

      this.hideAllQuantityIncrementMessages(true);
      super.close(callback);

      if (!this.cartDrawer?.hasAttribute('open')) return;

      this.overlay.classList.add('is-visible');

      // After an edit the cart has re-rendered and the opener is detached, so fall back to the
      // first line item, as the theme does after a cart quantity change.
      const focusTarget = this.cartDrawer.contains(opener)
        ? opener
        : this.cartDrawer.querySelector('.js-item-name');

      trapFocus(this.cartDrawer, focusTarget || this.cartDrawer);
    }

    /**
     * Returns the alternate product template view used to load drawer content.
     * @returns {string}
     */
    getAssortmentContentView() {
      return this.dataset.assortmentContentView || 'assortment-drawer-content';
    }

    /**
     * Fetches assortment drawer content for a product (Liquid-rendered HTML + rules JSON).
     * @param {string} handle - Product handle.
     * @param {string} view - Alternate product template view suffix.
     * @returns {Promise<{ fieldValues: object, contentHtml: string }>}
     */
    static async fetchProductData(handle, view) {
      const url = `/products/${encodeURIComponent(handle)}?view=${encodeURIComponent(view)}`;
      const response = await fetch(url);

      if (!response.ok) throw new Error(response.status);

      const html = await response.text();
      const content = AssortmentDrawer.parseAssortmentContentFromHtml(html);

      if (!content?.fieldValues) throw new Error('Assortment drawer data not found');
      if (!content.contentHtml?.trim()) throw new Error('Assortment drawer content not found');
      if (!content.contentHtml.includes('assortment-drawer__component')) {
        throw new Error('Assortment drawer components not found');
      }

      return content;
    }

    /**
     * Parses field values JSON and full drawer body HTML from the content template response.
     * @param {string} html - Product template HTML.
     * @returns {{ fieldValues: object|null, contentHtml: string }}
     */
    static parseAssortmentContentFromHtml(html) {
      const parser = new DOMParser();
      const parsed = parser.parseFromString(html, 'text/html');
      const root = parsed.querySelector('[data-assortment-drawer-content]');
      const script = root?.querySelector('script[type="application/json"][data-assortment-field-values]')
        || parsed.querySelector('script[type="application/json"][data-assortment-field-values]');

      let fieldValues = null;

      if (script?.textContent) {
        fieldValues = JSON.parse(script.textContent.trim());
      }
      return {
        fieldValues,
        contentHtml: root?.outerHTML?.trim() || ''
      };
    }

    /**
     * Parses a per-unit price to cents (display string, number, or Shopify money object).
     * @param {string|number|object} value - Price from tier JSON or data attribute.
     * @returns {number|null}
     */
    static parsePriceToCents(value) {
      if (value === '' || value == null) return null;

      if (typeof value === 'object') {
        if (value.amount != null) return AssortmentDrawer.parsePriceToCents(value.amount);
        return null;
      }

      if (typeof value === 'number') {
        if (!Number.isFinite(value)) return null;
        return Number.isInteger(value) && value >= 1000
          ? value
          : Math.round(value * 100);
      }

      const str = String(value).trim().replace(/[^0-9.-]/g, '');
      if (!str) return null;

      const dollars = Number.parseFloat(str);
      if (Number.isNaN(dollars)) return null;

      return Math.round(dollars * 100);
    }

    /**
     * Resolves tier price from effective_price or price.
     * @param {{ effective_price?: string|number, price?: string|number|null }} tier
     * @returns {string|number|null|undefined}
     */
    static resolveTierPrice(tier) {
      if (!tier) return null;
      if(tier.effective_price != null && tier.effective_price !== ''){
        return tier.effective_price;
      } else {
        return tier.price;
      }
    }
    
    /**
     * Normalizes tiers pricing array for tier lookup.
     * @param {Array<{ cases: string|number, price: string|number|null }>} tiersPricing
     * @returns {Array<{ cases: string|number, price: string|number }>|null}
     */
    static normalizeTiersPricing(tiersPricing) {
      if (!Array.isArray(tiersPricing) || !tiersPricing.length) return null;

      return tiersPricing.map((tier) => ({
        cases: tier.cases,
        price: AssortmentDrawer.resolveTierPrice(tier)
      }));
    }

    /**
     * Reads per-SKU max addable quantity from data attribute or input max (inventory cap).
     * @param {HTMLInputElement} input - Quantity input element.
     * @returns {number|null}
     */
    static getStockMaxAddable(input) {
      const dataMax = input.dataset.maxAddable;
      if (dataMax !== '' && dataMax != null) {
        const parsed = Number.parseInt(dataMax, 10);
        if (!Number.isNaN(parsed)) return parsed;
      }

      if (input.hasAttribute('max') && input.max !== '') {
        const parsed = Number.parseInt(input.max, 10);
        if (!Number.isNaN(parsed)) return parsed;
      }

      return null;
    }

    /**
     * Max quantity for an input: stock cap for case qty; stock and assortment cap for components.
     * @param {HTMLInputElement} input - Quantity input element.
     * @returns {number|null}
     */
    getInputMaxAddable(input) {
      if (!input?.matches('.js-assortment-component-qty')) {
        return AssortmentDrawer.getStockMaxAddable(input);
      }

      const stockMax = AssortmentDrawer.getStockMaxAddable(input);
      if (this.assortmentMinNum == null) return stockMax;

      const cap = this.getRequiredMinimum();
      const currentValue = Number.parseInt(input.value, 10) || 0;
      const totalOthers = this.getTotalQuantity() - currentValue;
      // Lowering the case count can leave the selection over the cap. The shopper reduces the rows
      // themselves, so a row is never pushed below its committed quantity, only kept from rising.
      const committed = Number.parseInt(input.dataset.committedQty, 10) || 0;
      const assortmentMax = Math.max(0, cap - totalOthers, committed);

      if (stockMax == null) return assortmentMax;
      return Math.min(stockMax, assortmentMax);
    }

    /**
     * Reads minimum quantity from the input min attribute.
     * @param {HTMLInputElement} input - Quantity input element.
     * @returns {number}
     */
    static getInputMin(input) {
      if (!input?.hasAttribute('min') || input.min === '') return 0;
      const parsed = Number.parseInt(input.min, 10);
      return Number.isNaN(parsed) ? 0 : parsed;
    }

    /**
     * Populates the drawer from Liquid-rendered content and field value rules.
     * @param {{ fieldValues: object, contentHtml: string }} content - Parsed template response.
     * @param {{ quantity?: number|string, components?: string }} [options] - Initial values after
     * inject.
     */
    populateDrawer(content, options = {}) {
      const { fieldValues, contentHtml } = content;
      const emptyEl = this.querySelector('.js-assortment-drawer-empty');
      const mountEl = this.querySelector('.js-assortment-drawer-mount');

      if (!fieldValues || !contentHtml || !emptyEl || !mountEl) return;

      this.parkOverCapWarning();
      mountEl.innerHTML = contentHtml;
      const caseQtyRow = this.querySelector('.assortment-drawer__case-quantity');
      if (caseQtyRow && this.overCapWarning) caseQtyRow.append(this.overCapWarning);

      const footerEl = this.querySelector('.js-assortment-drawer-footer');
      if (footerEl) footerEl.hidden = false;

      this.assortmentData = fieldValues;
      // A prefilled selection already fills the case, so the warning would fire on open. It
      // belongs to changes the shopper makes, not to the state the drawer opens in.
      this.isPopulating = true;
      this.applyAssortmentRules(this.getAssortmentRules(fieldValues));
      // Case quantity first: it sets the cap that component quantities clamp against.
      this.setCaseQuantity(options.quantity, { silent: true });

      if (this.isEditing) {
        const editedQuantities = AssortmentDrawer.parseComponentQuantities(options.components);
        // Caps before values, so the prefill is not clamped by stock the edit itself is holding.
        this.releaseEditedStock(editedQuantities);
        this.setComponentQuantities(editedQuantities);
      }

      this.initQuantityInputs();
      this.refreshDrawerPricing();
      this.isPopulating = false;
      this.setErrorMsgState();
      emptyEl.hidden = true;
      mountEl.hidden = false;
    }

    /**
     * Whether the drawer is editing a bundle that is already in the cart.
     * @returns {boolean}
     */
    get isEditing() {
      return this.mode === MODE_EDIT;
    }

    /**
     * Sets the drawer mode, and the copy that depends on it.
     * @param {string} [mode] - 'edit' when opened from a cart line, otherwise 'add'.
     */
    setMode(mode) {
      this.mode = mode === MODE_EDIT ? MODE_EDIT : MODE_ADD;

      const headingEl = this.querySelector('.js-assortment-drawer-heading');
      const heading = this.isEditing ? this.dataset.drawerHeaderEdit : this.dataset.drawerHeader;
      if (headingEl && heading) headingEl.textContent = heading;

      const submitText = this.isEditing ? this.dataset.stringEditCart : this.dataset.stringAddToCart;
      if (!submitText) return;

      this.querySelectorAll('.js-assortment-drawer-add-text').forEach((el) => {
        el.textContent = submitText;
      });
    }

    /**
     * @param {string} [components] - Comma separated `variantId:quantity` pairs.
     * @returns {Map<number, number>} Quantity by component variant id.
     */
    static parseComponentQuantities(components) {
      const quantities = new Map();
      if (!components) return quantities;

      components.split(',').forEach((pair) => {
        const [rawId, rawQuantity] = pair.split(':');
        const variantId = Number.parseInt(rawId, 10);
        const quantity = Number.parseInt(rawQuantity, 10);

        if (!variantId || !(quantity > 0)) return;

        quantities.set(variantId, quantity);
      });

      return quantities;
    }

    /**
     * Finds the quantity input of a component row.
     * @param {number} variantId - Component variant id.
     * @returns {HTMLInputElement|null}
     */
    componentInput(variantId) {
      return this.querySelector(
        `.assortment-drawer__component .js-assortment-component-qty[data-component-variant-id="${variantId}"]`
      );
    }

    /**
     * Returns the stock held by the bundle being edited to the rows it came from. Its line is
     * removed before the replacement is added, so counting it as in-cart would cap the shopper
     * below the quantities they already have. Inventory is still the ceiling: the units are only
     * free to re-add while stock has not been reduced under the cart since the bundle was added.
     * @param {Map<number, number>} quantities - Quantities of the bundle being edited.
     */
    releaseEditedStock(quantities) {
      quantities.forEach((quantity, variantId) => {
        const input = this.componentInput(variantId);
        const cap = Number.parseInt(input?.dataset.maxAddable, 10);
        if (Number.isNaN(cap)) return;

        const inventory = Number.parseInt(input.dataset.inventoryQuantity, 10);
        const released = Number.isNaN(inventory)
          ? cap + quantity
          : Math.min(inventory, cap + quantity);

        input.dataset.maxAddable = released;
        input.max = released;

        if (released <= 0) return;

        // The buttons follow from the input in updateComponentPlusButton.
        input.disabled = false;
        input.closest('.assortment-drawer__component')
          ?.querySelector('.assortment-drawer__stock-label')
          ?.remove();
      });
    }

    /**
     * Prefills the component rows with the quantities of the bundle being edited.
     * @param {Map<number, number>} quantities - Quantities of the bundle being edited.
     */
    setComponentQuantities(quantities) {
      quantities.forEach((quantity, variantId) => {
        const input = this.componentInput(variantId);
        if (input) input.value = quantity;
      });
    }

    /**
     * Initializes quantity inputs after Liquid-rendered markup is injected.
     */
    initQuantityInputs() {
      this.hideAllQuantityIncrementMessages(true);
      this.clampAllComponentQuantities();
      this.querySelectorAll('.js-assortment-case-qty, .assortment-drawer__component .js-assortment-component-qty')
        .forEach((input) => {
          this.updateComponentPlusButton(input);
        });
      this.querySelectorAll('.assortment-drawer__component .js-assortment-component-qty').forEach((input) => {
        this.initManualQuantityRestrictions(input);
      });
      this.commitComponentQuantities();
    }

    /**
     * Records each component row's settled quantity, the floor the assortment cap clamps it to.
     */
    commitComponentQuantities() {
      this.querySelectorAll('.assortment-drawer__component .js-assortment-component-qty').forEach((input) => {
        input.dataset.committedQty = String(Number.parseInt(input.value, 10) || 0);
      });
    }
    /**
     * Corrects manually typed component quantities to the row increment and the addable cap.
     * @param {HTMLInputElement} input - Component quantity input.
     */
    initManualQuantityRestrictions(input) {
      const inc = Number.parseInt(input.dataset.increment, 10) || 1;

      input.addEventListener('change', () => {
        const value = Number.parseInt(input.value, 10);
        if (Number.isNaN(value)) return;

        const max = this.getInputMaxAddable(input);
        const hasMax = max != null && !Number.isNaN(max);
        const exceedsMax = hasMax && value > max;

        if (!exceedsMax && (inc <= 1 || value % inc === 0)) return;

        // Halfway rounds up, so 30 with an increment of 12 lands on 36 rather than 24. Anything
        // below a single increment still bumps up to one, as rounding to nearest would clear it.
        let corrected = inc > 1 ? Math.max(inc, Math.round(value / inc) * inc) : value;

        // Rounding up must not outrun the stock or case cap, so fall back to the increment below.
        if (hasMax && corrected > max) {
          corrected = inc > 1 ? Math.floor(max / inc) * inc : max;
        }

        input.value = corrected;
        input.dataset.pendingQtyCorrection = String(corrected);

        if (exceedsMax) {
          input.dataset.pendingQtyCap = String(max);
        } else {
          delete input.dataset.pendingQtyCap;
        }
      });
    }

    /**
     * Shows the matching message when a corrected manual entry survived clamping.
     * @param {HTMLInputElement} input - Component quantity input.
     */
    maybeShowQuantityIncrementMessage(input) {
      const pending = input.dataset.pendingQtyCorrection;
      const cap = input.dataset.pendingQtyCap;
      if (pending == null) return;

      delete input.dataset.pendingQtyCorrection;
      delete input.dataset.pendingQtyCap;

      const finalValue = Number.parseInt(input.value, 10) || 0;
      if (finalValue <= 0 || finalValue !== Number.parseInt(pending, 10)) return;

      const region = input.closest('.assortment-drawer__component-row')
        ?.querySelector('.assortment-drawer__qty-message-region');
      if (!region) return;

      // A capped entry is the more specific of the two, so it wins when both would apply.
      const inc = Number.parseInt(input.dataset.increment, 10) || 1;
      const text = cap == null
        ? region.dataset.qtyRoundedMessage
        : this.getQuantityCapMessage(cap, finalValue, inc);

      if (text) {
        this.scheduleQuantityIncrementMessage(region, text);
      }
    }

    /**
     * Fills the "exceeds available" message from its section setting.
     * @param {number|string} available - Quantity the row could still take.
     * @param {number} newQty - Quantity the row was set to instead.
     * @param {number} increment - Row quantity increment.
     * @returns {string}
     */
    getQuantityCapMessage(available, newQty, increment) {
      const template = this.dataset.qtyCapTemplate || '';
      if (!template) return '';

      return template
        .replace(/\[available\]/g, available)
        .replace(/\[new_qty\]/g, newQty)
        .replace(/\[inc\]/g, increment);
    }

    /**
     * Schedules a quantity message after blur so focus announcements settle first.
     * @param {HTMLElement} region - Persistent live region for the row.
     * @param {string} text - Message to announce and display.
     */
    scheduleQuantityIncrementMessage(region, text) {
      if (!region) return;

      if (this.quantityMessageShowTimeoutId) {
        clearTimeout(this.quantityMessageShowTimeoutId);
        this.quantityMessageShowTimeoutId = null;
      }

      this.pendingQuantityMessageRegion = region;
      this.pendingQuantityMessageText = text;
      this.quantityMessageShowTimeoutId = setTimeout(() => {
        this.quantityMessageShowTimeoutId = null;
        const pendingRegion = this.pendingQuantityMessageRegion;
        const pendingText = this.pendingQuantityMessageText;
        this.pendingQuantityMessageRegion = null;
        this.pendingQuantityMessageText = null;
        if (pendingRegion) {
          this.showQuantityIncrementMessage(pendingRegion, pendingText);
        }
      }, QUANTITY_ROUNDED_ANNOUNCE_DELAY_MS);
    }

    /**
     * Inserts the visible banner into the row live region to trigger an announcement.
     * @param {HTMLElement} region - Persistent live region for the row.
     * @param {string} [message] - Message to display (defaults to the row rounding message).
     */
    showQuantityIncrementMessage(region, message) {
      if (!region) return;

      const text = message || region.dataset.qtyRoundedMessage || '';
      if (!text) return;

      const existingBanner = region.querySelector('.assortment-drawer__qty-message');
      if (existingBanner?.textContent === text) return;

      if (this.activeQuantityMessageRegion && this.activeQuantityMessageRegion !== region) {
        this.hideQuantityIncrementMessage(this.activeQuantityMessageRegion, true);
      }

      this.hideQuantityIncrementMessage(region, true);

      region.replaceChildren();

      const banner = document.createElement('div');
      banner.className = 'assortment-drawer__qty-message';
      banner.textContent = text;
      region.appendChild(banner);

      this.classList.add('is-qty-message-active');
      this.activeQuantityMessageRegion = region;

      requestAnimationFrame(() => {
        banner.classList.add('is-visible');
      });

      this.quantityMessageHideTimeoutId = setTimeout(() => {
        this.hideQuantityIncrementMessage(region);
      }, QUANTITY_ROUNDED_MESSAGE_MS);
    }

    /**
     * Hides the active quantity increment message.
     * @param {HTMLElement} [region] - Live region to clear (defaults to active region).
     * @param {boolean} [immediate=false] - Skip the slide-up animation.
     */
    hideQuantityIncrementMessage(region, immediate = false) {
      if (this.quantityMessageShowTimeoutId) {
        clearTimeout(this.quantityMessageShowTimeoutId);
        this.quantityMessageShowTimeoutId = null;
        this.pendingQuantityMessageRegion = null;
        this.pendingQuantityMessageText = null;
      }

      if (this.quantityMessageHideTimeoutId) {
        clearTimeout(this.quantityMessageHideTimeoutId);
        this.quantityMessageHideTimeoutId = null;
      }

      const target = region || this.activeQuantityMessageRegion;
      if (!target) return;

      const banner = target.querySelector('.assortment-drawer__qty-message');
      if (!banner) {
        if (this.activeQuantityMessageRegion === target) {
          this.activeQuantityMessageRegion = null;
        }
        this.updateQuantityMessageActiveState();
        return;
      }

      if (immediate || !banner.classList.contains('is-visible')) {
        target.replaceChildren();
        if (this.activeQuantityMessageRegion === target) {
          this.activeQuantityMessageRegion = null;
        }
        this.updateQuantityMessageActiveState();
        return;
      }

      banner.classList.remove('is-visible');
      banner.classList.add('is-hiding');

      const onTransitionEnd = (evt) => {
        if (evt.target !== banner || evt.propertyName !== 'max-height') return;

        banner.removeEventListener('transitionend', onTransitionEnd);
        target.replaceChildren();

        if (this.activeQuantityMessageRegion === target) {
          this.activeQuantityMessageRegion = null;
        }
        this.updateQuantityMessageActiveState();
      };

      banner.addEventListener('transitionend', onTransitionEnd);
    }

    /**
     * Hides all row quantity increment messages.
     * @param {boolean} [immediate=false] - Skip slide-up animations.
     */
    hideAllQuantityIncrementMessages(immediate = false) {
      this.querySelectorAll('.assortment-drawer__qty-message-region').forEach((region) => {
        this.hideQuantityIncrementMessage(region, immediate);
      });
    }

    /**
     * Toggles drawer state used to hide scrollbars while a quantity message is visible.
     */
    updateQuantityMessageActiveState() {
      const hasVisibleMessage = Array.from(this.querySelectorAll('.assortment-drawer__qty-message-region'))
        .some((region) => region.querySelector('.assortment-drawer__qty-message'));

      this.classList.toggle('is-qty-message-active', hasVisibleMessage);
    }
    /**
     * Sets the case quantity input after drawer content is injected.
     * @param {number|string|undefined|null} quantity - Desired case count (omitted below input min if invalid).
     * @param {{ silent?: boolean }} [opts] - When true, skips change event (caller runs refreshDrawerPricing).
     */
    setCaseQuantity(quantity, opts = {}) {
      const input = this.querySelector('.js-assortment-case-qty');
      if (!input) return;

      if (quantity !== '' && quantity != null) {
        const min = AssortmentDrawer.getInputMin(input);
        const value = Math.max(min, Number.parseInt(quantity, 10) || 0);
        input.value = value;
      }

      this.updateCaseQuantityUnit();
      this.updateCaseQuantityButtons(input);

      if (!opts.silent) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /**
     * Updates the case qty unit label (Case vs Cases) from data-unit-single / data-unit-multi.
     */
    updateCaseQuantityUnit() {
      const input = this.querySelector('.js-assortment-case-qty');
      const unitEl = input?.closest('.case-quantity-row__field')?.querySelector('.case-quantity-row__unit')
        || this.querySelector('.case-quantity-row__unit');

      if (!unitEl) return;

      const qty = Number.parseInt(input?.value, 10) || 0;
      const singleLabel = unitEl.dataset.unitSingle;
      const multiLabel = unitEl.dataset.unitMulti;

      if (qty === 1 && singleLabel) {
        unitEl.textContent = singleLabel;
      } else if (multiLabel) {
        unitEl.textContent = multiLabel;
      }
    }

    /**
     * Resolves minimum quantity and increment from assortment field values or section defaults.
     * @param {object} fieldValues - Assortment field values JSON.
     * @returns {{ minNum: number, inc: number }}
     */
    getAssortmentRules(fieldValues) {
      const defaultMin = Number.parseInt(this.dataset.defaultMinimum, 10) || 1;
      const defaultInc = Number.parseInt(this.dataset.defaultIncrement, 10) || 1;

      let minNum = defaultMin;
      if (fieldValues?.itemsPerCase != null && fieldValues.itemsPerCase !== '') {
        minNum = Number.parseInt(fieldValues.itemsPerCase, 10);
      }

      let inc = defaultInc;
      if (fieldValues?.assortmentStep != null && fieldValues.assortmentStep !== '') {
        inc = Number.parseInt(fieldValues.assortmentStep, 10);
      } else if (fieldValues?.inc != null && fieldValues.inc !== '') {
        inc = Number.parseInt(fieldValues.inc, 10);
      }

      return {
        minNum: minNum > 0 ? minNum : defaultMin,
        inc: inc > 0 ? inc : defaultInc
      };
    }

    /**
     * Applies minimum/increment rules to the drawer UI.
     * @param {{ minNum: number, inc: number }} rules - Assortment quantity rules.
     */
    applyAssortmentRules(rules) {
      this.assortmentMinNum = rules.minNum;
      this.assortmentInc = rules.inc;

      this.updateDrawerDescription();
      this.updateAddButtonState();
    }

    /**
     * Placeholder values shared by the drawer description and case-full warning.
     * @returns {{ min_num: number, inc: number, qty: number, case_qty: number, case_var: string }}
     */
    getMessagePlaceholders() {
      const caseQty = this.getCaseQuantity();
      const totalQty = this.getSubtotalMultiplierQuantity();
      const caseVarSingle = this.dataset.caseVarSingle || 'case';
      const caseVarPlural = this.dataset.caseVarPlural || 'cases';
      const defaultInc = Number.parseInt(this.dataset.defaultIncrement, 10) || 1;
      const minNum = this.assortmentMinNum ?? this.getUnitsPerCase();
      const inc = this.assortmentInc ?? defaultInc;

      return {
        min_num: minNum,
        inc,
        qty: totalQty,
        case_qty: caseQty,
        case_var: caseQty === 1 ? caseVarSingle : caseVarPlural
      };
    }

    /**
     * Replaces [min_num], [inc], [qty], [case_qty], and [case_var] in a template string.
     * @param {string} template
     * @returns {string}
     */
    applyMessagePlaceholders(template) {
      const placeholders = this.getMessagePlaceholders();

      return template
        .replace(/\[min_num\]/g, placeholders.min_num)
        .replace(/\[inc\]/g, placeholders.inc)
        .replace(/\[qty\]/g, placeholders.qty)
        .replace(/\[case_qty\]/g, placeholders.case_qty)
        .replace(/\[case_var\]/g, placeholders.case_var);
    }

    /**
     * Units per case from applied rules or field values (for pricing before rules finish applying).
     * @returns {number}
     */
    getUnitsPerCase() {
      if (this.assortmentMinNum != null && this.assortmentMinNum > 0) {
        return this.assortmentMinNum;
      }

      const parsed = Number.parseInt(this.assortmentData?.itemsPerCase, 10);
      return Number.isNaN(parsed) || parsed <= 0 ? 0 : parsed;
    }

    /**
     * Tier rows from field values JSON (`cases` is a unit-count threshold,
     * Liquid-resolved effective_price).
     * @returns {Array<{ cases: string|number, price: string|number }>}
     */
    getTierPricingSource() {
      return AssortmentDrawer.normalizeTiersPricing(this.assortmentData?.tiersPricing) || [];
    }

    /**
     * Updates subtotal, total count, and add-button state after qty or rules change.
     */
    refreshDrawerPricing() {
      this.updateDrawerDescription();
      this.updateSubtotal();
      this.updateAddButtonState();
      this.updateWarningMessage();
    }

    /**
     * Replaces template placeholders in the case-full and over-cap warning messages.
     */
    updateWarningMessage() {
      [
        [this.warningMessage, this.dataset.warningMessageTemplate],
        [this.overCapWarning, this.dataset.overCapWarningTemplate]
      ].forEach(([warningEl, template]) => {
        const bodyEl = warningEl?.querySelector('[data-warning-message-body]');
        if (!bodyEl || !template) return;

        bodyEl.textContent = this.applyMessagePlaceholders(template);
      });
    }

    /**
     * Returns the over-cap warning to the footer so replacing the injected content doesn't
     * destroy it.
     */
    parkOverCapWarning() {
      if (this.overCapWarning && this.overCapWarningHome) {
        this.overCapWarningHome.prepend(this.overCapWarning);
      }
    }

    /**
     * Shows the over-cap warning while the selection exceeds cases × units per case.
     * @param {boolean} visible - Whether the warning is visible.
     */
    setOverCapWarningVisibility(visible) {
      if (!this.overCapWarning) return;

      this.overCapWarning.hidden = !visible;
    }

    /**
     * Milliseconds to keep the case-full warning visible before auto-hiding, or 0 to leave it open.
     * @returns {number}
     */
    getWarningHideTimeout() {
      if (this.dataset.warningAutoHide !== 'true') return 0;

      const seconds = Number.parseFloat(this.dataset.warningHideDuration);
      if (Number.isNaN(seconds) || seconds <= 0) return 5000;

      return Math.round(seconds * 1000);
    }

    /**
     * Shows or hides the case-full warning banner.
     * @param {boolean} visible - Whether the warning is visible.
     * @param {number} timeoutToHide - Time in milliseconds to hide the warning automatically after *
     * showing it. Default is 0 (no timeout).
     */
    setWarningVisibility(visible = true, timeoutToHide = 0) {
      if (!this.warningMessage) return;

      // Clear any pending delayed warning show timeout
      if (this.warningDelayedShowTimeoutId) {
        clearTimeout(this.warningDelayedShowTimeoutId);
        this.warningDelayedShowTimeoutId = null;
      }

      if (this.warningHideTimeoutId) {
        clearTimeout(this.warningHideTimeoutId);
        this.warningHideTimeoutId = null;
      }

      if (visible && this.isPopulating) {
        this.warningMessage.hidden = true;
        return;
      }

      if (timeoutToHide) {
        this.warningMessage.hidden = !visible;
        this.warningHideTimeoutId = setTimeout(() => {
          this.warningMessage.hidden = visible;
          this.warningHideTimeoutId = null;
        }, timeoutToHide);
      } else {
        this.warningMessage.hidden = !visible;
      }
    }

    /**
     * Replaces template placeholders in the drawer description.
     */
    updateDrawerDescription() {
      const descEl = this.querySelector('.js-assortment-drawer-description');
      const template = this.dataset.descriptionTemplate;

      if (!descEl || !template) return;

      descEl.innerHTML = this.applyMessagePlaceholders(template);
    }

    /**
     * Returns the total quantity selected across all variants.
     * @returns {number}
     */
    getTotalQuantity() {
      return Array.from(this.querySelectorAll('.assortment-drawer__component .js-assortment-component-qty'))
        .reduce((sum, input) => sum + (Number.parseInt(input.value, 10) || 0), 0);
    }

    /**
     * Returns the selected number of cases.
     * @returns {number}
     */
    getCaseQuantity() {
      const input = this.querySelector('.js-assortment-case-qty');
      return Number.parseInt(input?.value, 10) || 0;
    }

    /**
     * Minimum total component units required (cases × units per case).
     * @returns {number}
     */
    getRequiredMinimum() {
      const unitsPerCase = this.getUnitsPerCase();
      if (!unitsPerCase) return 0;
      return this.getCaseQuantity() * unitsPerCase;
    }

    /**
     * Total units for the subtotal multiplier (cases × units per case).
     * @returns {number}
     */
    getSubtotalMultiplierQuantity() {
      return this.getCaseQuantity() * this.getUnitsPerCase();
    }

    /**
     * Per-unit price in cents from tier rules (case qty vs tier `cases` threshold).
     * `cases` in the JSON is a total-unit count, so it's divided by units per case
     * to get the true case threshold before comparing to the case qty selector.
     * @returns {number}
     */
    getTierUnitPriceCents() {
      const fallbackUnitCents = Number.parseInt(this.assortmentData?.parentVariantPrice, 10) || 0;
      const tiers = this.getTierPricingSource();

      if (!tiers.length) return fallbackUnitCents;

      const caseQty = this.getCaseQuantity();
      const unitsPerCase = this.getUnitsPerCase();

      const parsedTiers = tiers
        .map((tier) => {
          const rawCases = Number.parseInt(tier.cases, 10) || 0;
          const threshold = unitsPerCase > 0 ? rawCases / unitsPerCase : rawCases;
          return {
            threshold,
            cents: AssortmentDrawer.parsePriceToCents(tier.price)
          };
        })
        .filter((tier) => tier.threshold > 0 && tier.cents != null);

      if (!parsedTiers.length) return fallbackUnitCents;

      const match = [...parsedTiers]
        .sort((a, b) => b.threshold - a.threshold)
        .find((tier) => caseQty >= tier.threshold);

      return match?.cents ?? fallbackUnitCents;
    }

    /**
     * Subtotal in cents: unit price times total selected units, unrounded.
     * @returns {number}
     */
    getSubtotalCents() {
      const unitPriceCents = this.getTierUnitPriceCents();
      const totalUnits = this.getSubtotalMultiplierQuantity();

      return totalUnits * unitPriceCents;
    }

    /**
     * Updates footer subtotal line from case qty and tiered per-unit pricing.
     */
    updateSubtotal() {
      const multiplierEl = this.querySelector('[data-subtotal-multiplier]');
      const valueEl = this.querySelector('[data-subtotal-value]');
      if (!multiplierEl || !valueEl) return;

      const multiplierQty = this.getSubtotalMultiplierQuantity();
      const unitPriceCents = this.getTierUnitPriceCents();
      // What the shopper is counting, e.g. "blankets", from the same metafield the cart line reads.
      // The Shopify product type reads "Eros Assortment" on every one of these, which describes
      // nothing.
      const unitLabel = (this.assortmentData?.unitsOfMeasure
        || this.dataset.stringUnitsPlural
        || '').toLowerCase();
      const priceFormatted = AssortmentDrawer.formatMoney(unitPriceCents);
      const template = this.dataset.subtotalMultiplierTemplate || '';

      multiplierEl.textContent = template
        .replace(/\[qty\]/g, multiplierQty)
        .replace(/\[product_type\]/g, unitLabel)
        .replace(/\[price\]/g, priceFormatted);

      valueEl.textContent = AssortmentDrawer.formatMoney(this.getSubtotalCents());
    }

    /**
     * Formats a price in cents using the theme money format when available.
     * @param {number} cents - Price in cents.
     * @returns {string}
     */
    static formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, theme?.moneyFormat || Shopify.money_format);
      }

      return `$${(cents / 100).toFixed(2)}`;
    }

    /**
     * Updates the component total counter (selected units / required minimum).
     */
    updateTotalCount() {
      const numeratorEl = this.querySelector('[data-numerator]');
      const denominatorEl = this.querySelector('[data-denominator]');
      if (!numeratorEl || !denominatorEl) return;

      numeratorEl.textContent = this.getTotalQuantity();
      denominatorEl.textContent = this.getRequiredMinimum();
    }

    /**
     * Enables the add button when total quantity meets the minimum.
     */
    updateAddButtonState() {
      this.updateTotalCount();

      const submitBtn = this.querySelector('.js-assortment-drawer-add');
      if (!submitBtn || this.assortmentMinNum == null) return;

      const selected = this.getTotalQuantity();
      const needed = this.getRequiredMinimum();
      const isComplete = selected === needed && selected > 0;

      this.setOverCapWarningVisibility(selected > needed);

      if (isComplete) {
        submitBtn.removeAttribute('disabled');
        submitBtn.removeAttribute('aria-disabled');
        
        // A row message that is pending or on screen would be buried by the footer warning.
        const hasPendingQtyMessage = this.pendingQuantityMessageRegion != null;
        const hasActiveQtyMessage = this.activeQuantityMessageRegion
          && this.activeQuantityMessageRegion.querySelector('.assortment-drawer__qty-message');

        if (hasPendingQtyMessage || hasActiveQtyMessage) {
          if (this.warningDelayedShowTimeoutId) {
            clearTimeout(this.warningDelayedShowTimeoutId);
          }

          this.warningDelayedShowTimeoutId = setTimeout(() => {
            this.setWarningVisibility(true, this.getWarningHideTimeout());
            this.warningDelayedShowTimeoutId = null;
          }, CASE_FULL_WARNING_DELAY_MS);
        } else {
          this.setWarningVisibility(true, this.getWarningHideTimeout());
        }
      } else {
        submitBtn.setAttribute('disabled', '');
        submitBtn.setAttribute('aria-disabled', 'true');
        this.setWarningVisibility(false);
      }
    }

    /**
     * Updates add button state when variant quantities change.
     * @param {object} evt - Event object.
     */
    handleQuantityChange(evt) {
      if (!evt.target.matches('.js-assortment-component-qty, .js-assortment-case-qty')) return;

      if (evt.target.matches('.js-assortment-case-qty')) {
        this.updateCaseQuantityUnit();
        this.updateCaseQuantityButtons(evt.target);
        this.setWarningVisibility(false);
      } else {
        this.clampComponentQuantity(evt.target);
        this.maybeShowQuantityIncrementMessage(evt.target);
        this.commitComponentQuantities();
      }

      this.updateAllComponentPlusButtons();
      this.refreshDrawerPricing();

      if (this.getTotalQuantity() < this.getRequiredMinimum()) {
        this.setWarningVisibility(false);
      }
    }

    /**
     * Clamps all component rows so total units do not exceed cases × units per case.
     */
    clampAllComponentQuantities() {
      const inputs = this.querySelectorAll('.assortment-drawer__component .js-assortment-component-qty');
      if (!inputs.length) return;

      let changed = true;
      let passes = 0;

      while (changed && passes < inputs.length + 1) {
        changed = false;
        passes += 1;
        inputs.forEach((input) => {
          const before = Number.parseInt(input.value, 10) || 0;
          this.clampComponentQuantity(input, false);
          const after = Number.parseInt(input.value, 10) || 0;
          if (after !== before) changed = true;
        });
      }
    }

    /**
     * Clamps a quantity input to its max addable amount.
     * @param {HTMLInputElement} input - Quantity input element.
     * @param {boolean} [updatePlusButton=true] - Whether to refresh the row plus button.
     */
    clampComponentQuantity(input, updatePlusButton = true) {
      const min = AssortmentDrawer.getInputMin(input);
      const max = this.getInputMaxAddable(input);
      let value = Number.parseInt(input.value, 10) || 0;

      if (value < min) value = min;
      if (max != null && !Number.isNaN(max) && value > max) value = max;

      input.value = value;

      if (updatePlusButton) {
        this.updateComponentPlusButton(input);
      }
    }

    /**
     * Refreshes plus/minus state on every component row (shared assortment cap).
     */
    updateAllComponentPlusButtons() {
      this.querySelectorAll('.assortment-drawer__component .js-assortment-component-qty')
        .forEach((input) => {
          this.updateComponentPlusButton(input);
        });
    }

    /**
     * Updates plus/minus buttons for the parent case quantity control.
     * @param {HTMLInputElement} [input] - Case quantity input (defaults to drawer case input).
     */
    updateCaseQuantityButtons(input = this.querySelector('.js-assortment-case-qty')) {
      if (!input?.matches('.js-assortment-case-qty')) return;

      const qtyWrap = input.closest('[data-assortment-qty]');
      const plusBtn = qtyWrap?.querySelector('.qty-input__btn[name="plus"]');
      const minusBtn = qtyWrap?.querySelector('.qty-input__btn[name="minus"]');
      const value = Number.parseInt(input.value, 10) || 0;
      const max = AssortmentDrawer.getStockMaxAddable(input);

      if (minusBtn) {
        minusBtn.disabled = value <= 1;
      }

      if (plusBtn) {
        plusBtn.disabled = input.disabled || (max != null && !Number.isNaN(max) && value >= max);
      }
    }

    /**
     * Disables the plus button when the component quantity has reached its limit.
     * @param {HTMLInputElement} input - Quantity input element.
     */
    updateComponentPlusButton(input) {
      if (input.matches('.js-assortment-case-qty')) {
        this.updateCaseQuantityButtons(input);
        return;
      }

      const qtyWrap = input.closest('[data-assortment-qty]');
      const plusBtn = qtyWrap?.querySelector('.qty-input__btn[name="plus"]');
      const minusBtn = qtyWrap?.querySelector('.qty-input__btn[name="minus"]');
      const min = AssortmentDrawer.getInputMin(input);
      const max = this.getInputMaxAddable(input);
      const value = Number.parseInt(input.value, 10) || 0;

      if (minusBtn) {
        minusBtn.disabled = input.disabled || value <= min;
      }

      if (!plusBtn) return;

      if (max == null) {
        plusBtn.disabled = input.disabled;
        return;
      }

      plusBtn.disabled = input.disabled || value >= max;
    }
    /**
     * Builds one cart line per selected component with bundle properties for the cart transform.
     * Line quantity is the component row qty only; _bundle_cases carries the case count for the bundle.
     * @returns {Array<{ id: number, quantity: number, properties: object }>}
     */
    buildAssortmentLinesFromComponentQuantities() {
      const bundleTotalCents = this.getSubtotalCents();
      const bundleTotalUnits = this.getSubtotalMultiplierQuantity();
      const unitsPerCase = this.getUnitsPerCase();
      const parentVariantId = String(this.assortmentData?.parentVariant || '');
      const parentSku = this.assortmentData?.parentSku || '';
      const bundleTitle = this.assortmentData?.productTitle || '';
      const bundleType = this.assortmentData.bundleType;
      const cases = this.getCaseQuantity();
      const unitsOfMeasure = this.assortmentData?.unitsOfMeasure || '';
      const lines = [];
      let componentIndex = 0;

      this.querySelectorAll('.assortment-drawer__component .js-assortment-component-qty').forEach((input) => {
        const unitQuantity = Number.parseInt(input.value, 10) || 0;
        if (unitQuantity <= 0) return;

        const row = input.closest('.assortment-drawer__component');
        const bundleAssortmentLabel = row?.dataset?.assortmentLabel || '';
        const variantId = Number.parseInt(
          row?.dataset?.componentVariantId || input.dataset?.componentVariantId,
          10
        );
        if (!variantId) return;

        componentIndex += 1;

        lines.push({
          id: variantId,
          quantity: unitQuantity,
          properties: {
            _bundle_parent_variant_id: parentVariantId,
            _bundle_parent_sku: parentSku,
            _bundle_role: 'component',
            _bundle_type: bundleType,
            _bundle_cases: String(cases),
            _bundle_total_cents: String(bundleTotalCents),
            _bundle_units_per_case: String(unitsPerCase),
            _bundle_total_units: String(bundleTotalUnits),
            _bundle_title: bundleTitle,
            _component_sku: row?.dataset?.sku || '',
            _component_index: String(componentIndex),
            _bundle_assortment_label: bundleAssortmentLabel,
            _type: bundleAssortmentLabel,
            _bundle_units_of_measure: unitsOfMeasure
          }
        });
      });

      return lines;
    }

    /**
     * Resets component quantity inputs after a successful add.
     */
    resetComponentQuantities() {
      this.querySelectorAll('.assortment-drawer__component .js-assortment-component-qty').forEach((input) => {
        input.value = 0;
      });
      this.commitComponentQuantities();
      this.updateAllComponentPlusButtons();
      this.refreshDrawerPricing();
    }

    /**
     * Adds each selected component variant to the cart with bundle line item properties.
     * @param {object} evt - Event object.
     */
    async handleAddToCart(evt) {
      evt.preventDefault();

      const submitBtn = this.querySelector('.js-assortment-drawer-add');

      this.updateAllComponentPlusButtons();

      this.setErrorMsgState();

      const selected = this.getTotalQuantity();
      const needed = this.getRequiredMinimum();

      if (selected !== needed || selected === 0) {
        const message = (this.dataset.stringExactTotal || '')
          .replace('[min_num]', needed);
        this.setErrorMsgState(message);
        return;
      }

      const lineItems = this.buildAssortmentLinesFromComponentQuantities();

      if (!lineItems.length || submitBtn?.disabled || submitBtn?.getAttribute('aria-disabled') === 'true') {
        return;
      }

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('is-loading');

      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      let lineRemoved = false;

      try {
        // A cart holds at most one bundle per assortment, so any existing one is dropped before
        // adding. An unchanged selection would otherwise merge into that same line, and removing it
        // afterwards would take the new bundle with it. The line is looked up here rather than taken
        // from the opener, so a page that has gone stale can't leave two bundles behind.
        const replaceLineKey = await this.fetchBundleLineKey();

        if (replaceLineKey) {
          await AssortmentDrawer.removeCartLine(replaceLineKey);
          lineRemoved = true;
        }

        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            // Reversed on purpose: the merged bundle comes back with its components in the reverse
            // of the order they were sent, so sending them backwards lands them in the order the
            // drawer showed. _component_index still numbers them in drawer order.
            items: [...lineItems].reverse(),
            sections,
            sections_url: window.location.pathname
          })
        });

        const data = await response.json();

        if (!response.ok) {
          const error = parseCartApiError(data, response.status);
          this.setErrorMsgState(this.formatSaveError(error, lineRemoved));
          if (lineRemoved) await this.refreshCart();
          return;
        }

        AssortmentDrawer.updateCartIcon(data);
        await this.refreshCart(data);

        this.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { items: lineItems }
        }));

        this.resetComponentQuantities();
        this.close();
      } catch (error) {
        this.setErrorMsgState(this.formatSaveError(error?.message, lineRemoved));
        if (lineRemoved) await this.refreshCart();
      } finally {
        submitBtn.classList.remove('is-loading');
        this.updateAddButtonState();
      }
    }

    /**
     * Finds the cart line already holding a bundle of this assortment, straight from the server.
     * The cart transform groups every component sharing a parent variant into one bundle and prices
     * the group from a single `_bundle_total_cents` snapshot, so two bundles of one assortment get
     * charged as one. The opener's own idea of the line is never trusted for this: it was rendered
     * when its page loaded, and a cart changed since then, or in another tab, leaves it describing a
     * line that has moved or gone.
     *
     * Read from the cart's rendered markup, where each assortment line carries its key on the edit
     * button, rather than from /cart.js, which does not describe transform-merged bundles.
     * @returns {Promise<string|null>} Line item key, or null when the cart holds no such bundle.
     */
    async fetchBundleLineKey() {
      const productHandle = this.assortmentData?.productHandle;
      if (!productHandle) return null;

      const sectionId = this.cartDrawer?.closest('.shopify-section')?.id.replace('shopify-section-', '');

      try {
        let html;

        if (sectionId) {
          // The drawer renders on every page, so its section carries the cart lines wherever the
          // shopper is standing. Cheaper than pulling the whole cart page.
          const response = await fetch(`?sections=${sectionId}`, { cache: 'no-store' });
          if (!response.ok) return null;
          html = (await response.json())[sectionId];
        } else {
          const response = await fetch(theme.routes.cart, { cache: 'no-store' });
          if (!response.ok) return null;
          html = await response.text();
        }

        if (!html) return null;

        const editBtn = new DOMParser().parseFromString(html, 'text/html')
          .querySelector(`.js-edit-assortment[data-product-handle="${productHandle}"]`);

        return editBtn?.dataset.lineKey || null;
      } catch (error) {
        console.error(error);
        return null;
      }
    }

    /**
     * Removes a cart line, so an edited bundle replaces the one it was opened from. Targets the
     * line by key rather than by line number, which shifts when the cart changes elsewhere.
     * @param {string} key - Line item key of the bundle being replaced.
     */
    static async removeCartLine(key) {
      await changeCartLine({ id: key, quantity: 0 });
    }

    /**
     * Re-renders the cart, in the drawer or on the cart page.
     * @param {object} [data] - Cart response holding rendered sections, if available.
     */
    async refreshCart(data) {
      // The cart drawer element exists on every page, so it can't tell us which cart the shopper
      // is looking at. The cart page renders its own cart-items, ahead of the drawer's in the DOM.
      const cartItems = document.querySelector('cart-items');
      const isPageCart = cartItems && !this.cartDrawer?.contains(cartItems);

      if (isPageCart) {
        if (cartItems.refresh) await cartItems.refresh();
        return;
      }

      if (!this.cartDrawer) return;

      if (data?.sections) {
        await this.cartDrawer.renderContents({ sections: data.sections }, theme.settings.afterAtc === 'drawer');
      } else {
        await this.cartDrawer.refresh(true);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Cart add response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');

      if (cartIconBubble && response.sections?.['cart-icon-bubble']) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Builds the error message shown when saving an assortment fails.
     * @param {string|Array|undefined} error - API or network error detail.
     * @param {boolean} lineRemoved - Whether an existing bundle was removed before the add.
     * @returns {string|Array} Message for setErrorMsgState.
     */
    formatSaveError(error, lineRemoved) {
      if (!lineRemoved) {
        return error || theme?.strings?.cartError;
      }

      const removedMessage = this.dataset.stringReplaceFailedRemoved;

      if (!error) return removedMessage;

      const apiError = Array.isArray(error) ? error.join(', ') : String(error);
      if (!apiError) return removedMessage;

      return [removedMessage, apiError];
    }

    /**
     * Shows or hides the assortment drawer error message.
     * @param {string|Array|false} [error=false] - Error to show.
     */
    setErrorMsgState(error = false) {
      const errorEl = this.querySelector('.js-assortment-drawer-error');
      if (!errorEl) return;

      errorEl.hidden = !error;

      if (!error) {
        errorEl.innerHTML = '';
        return;
      }

      errorEl.innerHTML = '';
      const errorArray = Array.isArray(error) ? error : [error];
      errorArray.forEach((err, index) => {
        if (index > 0) errorEl.insertAdjacentHTML('beforeend', '<br>');
        errorEl.insertAdjacentText('beforeend', err);
      });
    }

    /**
     * Resets the drawer to its empty state.
     */
    resetDrawerContent() {
      const emptyEl = this.querySelector('.js-assortment-drawer-empty');
      const mountEl = this.querySelector('.js-assortment-drawer-mount');
      const footerEl = this.querySelector('.js-assortment-drawer-footer');

      this.setMode(MODE_ADD);
      this.parkOverCapWarning();
      if (mountEl) {
        mountEl.innerHTML = '';
        mountEl.hidden = true;
      }
      if (emptyEl) emptyEl.hidden = false;
      if (footerEl) footerEl.hidden = true;

      const descEl = this.querySelector('.js-assortment-drawer-description');
      const multiplierEl = this.querySelector('[data-subtotal-multiplier]');
      const valueEl = this.querySelector('[data-subtotal-value]');
      if (descEl) descEl.innerHTML = '';
      if (multiplierEl) multiplierEl.textContent = '';
      if (valueEl) valueEl.textContent = '';

      this.setErrorMsgState();
      this.assortmentData = null;
      this.assortmentMinNum = null;
      this.assortmentInc = null;
      this.setWarningVisibility(false);
      this.setOverCapWarningVisibility(false);
    }

    /**
     * Closes the assortment drawer via the footer cancel button.
     * @param {object} evt - Event object.
     */
    handleCancelClick(evt) {
      evt.preventDefault();
      evt.stopPropagation();

      if (!document.querySelector('assortment-drawer')) {
        return;
      }
      document.dispatchEvent(new CustomEvent('dispatch:assortment-drawer:close'));
    }
  }

  customElements.define('assortment-drawer', AssortmentDrawer);
}
// Commenting out the open-assortment-drawer button for now. We may use later for PDPs
// if (!customElements.get('open-assortment-drawer')) {
//   class OpenAssortmentDrawer extends HTMLButtonElement {



//     constructor() {
//       super();
//       this.setAttribute('aria-haspopup', 'dialog');
//       if (!this.hasAttribute('type')) this.setAttribute('type', 'button');
//       this.handleClick = this.handleClick.bind(this);
//       this.addEventListener('click', this.handleClick);

//     }

//     disconnectedCallback() {
//       this.removeEventListener('click', this.handleClick);
//     }

//     /**
//      * Opens the assortment drawer.
//      * @param {object} evt - Event object.
//      */
//     handleClick(evt) {
//       evt.preventDefault();
//       evt.stopPropagation();

//       if (!document.querySelector('assortment-drawer')) {
//         return;
//       }

//       if (!this.dataset.productHandle) {
//         return;
//       }


//       const detail = {
//         opener: this,
//         productHandle: this.dataset.productHandle
//       };

//       if (this.dataset.quantity !== '' && this.dataset.quantity != null) {
//         detail.quantity = this.dataset.quantity;
//       }

//       document.dispatchEvent(new CustomEvent('dispatch:assortment-drawer:open', { detail }));
//     }
//   }

//   customElements.define('open-assortment-drawer', OpenAssortmentDrawer, { extends: 'button' });
// }
